import React from "react";
import PropTypes from "prop-types";
import { Col, Dropdown, Row } from "react-bootstrap";

const SERVICE_TYPE_ICON = "\uD83E\uDDE9";
const FACILITY_ICON = "\uD83C\uDFE2";
const SERVICE_TYPE_PREFIX_TO_TRIM = "Services for ";

function getDisplayServiceTypeLabel(t, serviceTypeKey) {
  const fullLabel = t(`serviceTypes.${serviceTypeKey}`);
  if (fullLabel.startsWith(SERVICE_TYPE_PREFIX_TO_TRIM)) {
    return fullLabel.slice(SERVICE_TYPE_PREFIX_TO_TRIM.length);
  }
  return fullLabel;
}

function ServiceTypeCard({
  t,
  serviceTypes,
  serviceTypeValue,
  onCardChange,
  alignRight,
  dropdownDirection,
  displayMode,
}) {
  const [open, setOpen] = React.useState(false);
  const isDisabled = displayMode !== "enabled";

  const toggleItem = (item) => {
    const updated = serviceTypeValue.includes(item)
      ? serviceTypeValue.filter((value) => value !== item)
      : [...serviceTypeValue, item];
    onCardChange(updated);
  };

  const renderTitle = () => {
    if (isDisabled) {
      return `${SERVICE_TYPE_ICON} ${t("serviceTypes.NotApplicable")}`;
    }
    if (serviceTypeValue.length === 0) {
      return `${SERVICE_TYPE_ICON} ${t("serviceTypes.Placeholder")}`;
    }
    if (serviceTypeValue.length === 1) {
      return `${SERVICE_TYPE_ICON} ${t("serviceTypes.Title")}: ${getDisplayServiceTypeLabel(
        t,
        serviceTypeValue[0]
      )}`;
    }
    return `${SERVICE_TYPE_ICON} ${t("serviceTypes.Title")}: ${serviceTypeValue.length}`;
  };

  return (
    <Dropdown
      drop={dropdownDirection}
      show={isDisabled ? false : open}
      onToggle={(nextOpen) => {
        if (!isDisabled) {
          setOpen(nextOpen);
        }
      }}
      className="filter-pill w-100"
    >
      <Dropdown.Toggle
        variant="light"
        className="dropdown-toggle-pill w-100 text-start"
        disabled={isDisabled}
      >
        {renderTitle()}
      </Dropdown.Toggle>
      {!isDisabled && (
        <Dropdown.Menu
          alignRight={alignRight}
          renderOnMount
          className="service-type-dropdown-menu"
        >
          {serviceTypes.map((serviceType) => (
            <Dropdown.Item
              key={serviceType}
              as="button"
              className="service-type-dropdown-item d-flex align-items-center"
              onClick={() => toggleItem(serviceType)}
            >
              <input
                type="checkbox"
                className="me-2 mr-2"
                readOnly
                checked={serviceTypeValue.includes(serviceType)}
              />
              <span
                className="service-type-dropdown-item-text"
                title={t(`serviceTypes.${serviceType}`)}
              >
                {getDisplayServiceTypeLabel(t, serviceType)}
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}

ServiceTypeCard.propTypes = {
  t: PropTypes.func.isRequired,
  serviceTypes: PropTypes.array.isRequired,
  serviceTypeValue: PropTypes.array.isRequired,
  onCardChange: PropTypes.func.isRequired,
  alignRight: PropTypes.bool,
  dropdownDirection: PropTypes.string,
  displayMode: PropTypes.string,
};

ServiceTypeCard.defaultProps = {
  alignRight: false,
  dropdownDirection: "down",
  displayMode: "na",
};

function DefaultFacilityCard({
  t,
  facilitiesData,
  facilitiesValue,
  onCardChange,
  alignRight,
  dropdownDirection,
}) {
  const [open, setOpen] = React.useState(false);
  const facilityTitle = t("facilities.Title");

  const toggleItem = (item) => {
    const itemValue = item[facilityTitle] || item.Facility || item;
    const isSelected = facilitiesValue.some((value) => {
      const selectedValue = value[facilityTitle] || value.Facility || value;
      return selectedValue === itemValue;
    });

    const updated = isSelected
      ? facilitiesValue.filter((value) => {
          const selectedValue = value[facilityTitle] || value.Facility || value;
          return selectedValue !== itemValue;
        })
      : [...facilitiesValue, item];

    onCardChange(updated);
  };

  const renderTitle = () => {
    if (facilitiesValue.length === 0) {
      return `${FACILITY_ICON} ${t("facilities.Placeholder")}`;
    }
    if (facilitiesValue.length === 1) {
      const name =
        facilitiesValue[0][facilityTitle] ||
        facilitiesValue[0].Facility ||
        facilitiesValue[0];
      return `${FACILITY_ICON} ${facilityTitle}: ${name}`;
    }
    return `${FACILITY_ICON} ${facilityTitle}: ${facilitiesValue.length}`;
  };

  return (
    <Dropdown
      drop={dropdownDirection}
      show={open}
      onToggle={(nextOpen) => setOpen(nextOpen)}
      className="filter-pill w-100"
    >
      <Dropdown.Toggle
        variant="light"
        className="dropdown-toggle-pill w-100 text-start"
      >
        {renderTitle()}
      </Dropdown.Toggle>
      <Dropdown.Menu alignRight={alignRight} renderOnMount>
        {facilitiesData.map((facility, index) => {
          const facilityName =
            facility[facilityTitle] || facility.Facility || facility;
          const isSelected = facilitiesValue.some((value) => {
            const selectedValue =
              value[facilityTitle] || value.Facility || value;
            return selectedValue === facilityName;
          });

          return (
            <Dropdown.Item
              key={`${facilityName}-${index}`}
              as="button"
              className="d-flex align-items-center"
              onClick={() => toggleItem(facility)}
            >
              <input
                type="checkbox"
                className="me-2 mr-2"
                readOnly
                checked={isSelected}
              />
              {facilityName}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

DefaultFacilityCard.propTypes = {
  t: PropTypes.func.isRequired,
  facilitiesData: PropTypes.array.isRequired,
  facilitiesValue: PropTypes.array.isRequired,
  onCardChange: PropTypes.func.isRequired,
  alignRight: PropTypes.bool,
  dropdownDirection: PropTypes.string,
};

DefaultFacilityCard.defaultProps = {
  alignRight: false,
  dropdownDirection: "down",
};

export default function ServiceFilterControls({
  t,
  regionProps,
  categoryProps,
  RegionCardComponent,
  CategoryCardComponent,
  serviceTypeDisplayMode,
  serviceTypes,
  serviceTypeValue,
  onServiceTypeChange,
  facilitiesData,
  facilitiesValue,
  onFacilityChange,
  FacilityCard,
  dropdownDirection,
  layout,
}) {
  const isWelcomeLayout = layout === "welcome";
  const columnWidth = isWelcomeLayout ? 6 : 3;
  const FacilityComponent = FacilityCard || DefaultFacilityCard;
  const rowClassName = isWelcomeLayout
    ? "filter-controls-horizontal filter-controls-welcome-layout g-2"
    : "filter-controls-horizontal g-2";

  return (
    <Row className={rowClassName}>
      <Col md={columnWidth} sm={6}>
        <RegionCardComponent
          {...regionProps}
          dropdownDirection={dropdownDirection}
        />
      </Col>
      <Col md={columnWidth} sm={6}>
        <CategoryCardComponent
          {...categoryProps}
          dropdownDirection={dropdownDirection}
        />
      </Col>
      <Col md={columnWidth} sm={6}>
        <ServiceTypeCard
          t={t}
          serviceTypes={serviceTypes}
          serviceTypeValue={serviceTypeValue}
          onCardChange={onServiceTypeChange}
          alignRight
          dropdownDirection={dropdownDirection}
          displayMode={serviceTypeDisplayMode}
        />
      </Col>
      <Col md={columnWidth} sm={6}>
        <FacilityComponent
          t={t}
          facilitiesData={facilitiesData}
          facilitiesValue={facilitiesValue}
          onCardChange={onFacilityChange}
          alignRight
          dropdownDirection={dropdownDirection}
        />
      </Col>
    </Row>
  );
}

ServiceFilterControls.propTypes = {
  t: PropTypes.func.isRequired,
  regionProps: PropTypes.object.isRequired,
  categoryProps: PropTypes.object.isRequired,
  RegionCardComponent: PropTypes.elementType.isRequired,
  CategoryCardComponent: PropTypes.elementType.isRequired,
  serviceTypeDisplayMode: PropTypes.string,
  serviceTypes: PropTypes.array.isRequired,
  serviceTypeValue: PropTypes.array.isRequired,
  onServiceTypeChange: PropTypes.func.isRequired,
  facilitiesData: PropTypes.array.isRequired,
  facilitiesValue: PropTypes.array.isRequired,
  onFacilityChange: PropTypes.func.isRequired,
  FacilityCard: PropTypes.elementType,
  dropdownDirection: PropTypes.string,
  layout: PropTypes.string,
};

ServiceFilterControls.defaultProps = {
  serviceTypeDisplayMode: "na",
  FacilityCard: null,
  dropdownDirection: "down",
  layout: "toolbar",
};
